import React from "react";
import { Tooltip } from "antd";

import { getInitials } from "../helpers/attendanceAdmin.helper";

import type { AttendanceResponse, AttendanceStatusFilter } from "../types";

interface AttendanceAdminStatsProps {
  data: AttendanceResponse | null;
  displayDate: string;
  activeStatus: AttendanceStatusFilter;
  onSelect: (status: AttendanceStatusFilter) => void;
}

interface StatCardProps {
  label: string;
  value: number;
  icon: string;
  iconClass: string;
  iconBg: string;
  accent: string;
  active?: boolean;
  onClick: () => void;
}

const StatCard: React.FC<StatCardProps> = ({
  label,
  value,
  icon,
  iconClass,
  iconBg,
  accent,
  active = false,
  onClick,
}) => (
  <div className="col-xl-3 col-md-6">
    <div
      onClick={onClick}
      style={{
        cursor: "pointer",
        background: "#ffffff",
        border: active ? `1.5px solid ${accent}` : "1px solid #e2e8f0",
        borderRadius: 12,
        padding: "18px 20px",
        transition: "all 0.18s ease",
        boxShadow: `0 1px 2px ${accent}1a`,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 4,
          background: accent,
          opacity: active ? 1 : 0,
          transition: "opacity 0.18s ease",
        }}
      />
      <div className="d-flex align-items-center justify-content-between">
        <div
          style={{
            width: 48,
            height: 48,
            borderRadius: 12,
            background: iconBg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <i className={`ti ${icon} ${iconClass}`} style={{ fontSize: 22 }} />
        </div>
        <div className="text-end">
          <p
            className="mb-1"
            style={{ fontSize: 12.5, color: "#64748b", fontWeight: 500 }}
          >
            {label}
          </p>
          <h3
            className="mb-0"
            style={{ fontSize: 24, fontWeight: 700, color: "#0f172a", lineHeight: 1 }}
          >
            {value}
          </h3>
        </div>
      </div>
    </div>
  </div >
);

const AVATAR_COLORS = [
  "#3b82f6",
  "#8b5cf6",
  "#ec4899",
  "#f97316",
  "#14b8a6",
  "#22c55e",
  "#eab308",
  "#06b6d4",
];

const colorForName = (name: string): string => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

interface AbsenteeStackProps {
  absentees: AttendanceResponse["absent_employees"];
}

const MAX_VISIBLE = 6;

const AbsenteeStack: React.FC<AbsenteeStackProps> = ({ absentees }) => {
  const total = absentees.length;
  if (total === 0) {
    return <span style={{ fontSize: 13, color: "#94a3b8" }}>No absentees today</span>;
  }
  const visible = absentees.slice(0, MAX_VISIBLE);
  const overflow = Math.max(0, total - MAX_VISIBLE);

  return (
    <div className="d-flex align-items-center">
      <div style={{ display: "inline-flex" }}>
        {visible.map((x, index) => (
          <Tooltip key={index} title={x.employee_name}>
            <div
              style={{
                width: 34,
                height: 34,
                borderRadius: "50%",
                border: "2px solid #ffffff",
                marginLeft: index === 0 ? 0 : -10,
                overflow: "hidden",
                position: "relative",
                background: colorForName(x.employee_name || ""),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#ffffff",
                fontSize: 12,
                fontWeight: 600,
                boxShadow: "0 1px 3px rgba(15, 23, 42, 0.1)",
                flexShrink: 0,
              }}
            >
              {x.userProfile ? (
                <img
                  src={x.userProfile}
                  alt={x.employee_name}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                getInitials(x.employee_name)
              )}
            </div>
          </Tooltip>
        ))}
      </div>
      {overflow > 0 && (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            height: 34,
            minWidth: 40,
            padding: "0 10px",
            marginLeft: 8,
            borderRadius: 17,
            background: "#fee2e2",
            color: "#b91c1c",
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          +{overflow}
        </span>
      )}
    </div>
  );
};

const AttendanceAdminStats: React.FC<AttendanceAdminStatsProps> = ({
  data,
  displayDate,
  activeStatus,
  onSelect,
}) => {
  const total = data?.total_employees || 0;
  const present = data?.present_count || 0;
  const late = data?.late_comers?.length || 0;
  const absent = data?.absent_count || 0;
  const absentees = data?.absent_employees || [];

  return (
    <div
      style={{
        background: "#ffffff",
        borderRadius: 12,
        border: "1px solid #e2e8f0",
        padding: "20px 24px",
        marginBottom: 16,
      }}
    >
      <div className="row align-items-center mb-4">
        <div className="col-md-6">
          <h4
            className="mb-1"
            style={{ fontSize: 17, fontWeight: 600, color: "#0f172a" }}
          >
            Attendance Details
          </h4>
          <p className="text-muted mb-0" style={{ fontSize: 13 }}>
            For {displayDate}
          </p>
        </div>
        <div className="col-md-6">
          <div className="d-flex align-items-center justify-content-md-end gap-3 flex-wrap">
            <span style={{ fontSize: 13, color: "#475569", fontWeight: 500 }}>
              Total Absentees today
            </span>
            <AbsenteeStack absentees={absentees} />
          </div>
        </div>
      </div>

      <div className="row g-3">
        <StatCard
          label="Total Employees"
          value={total}
          icon="ti-users"
          iconClass="text-primary"
          iconBg="#eff6ff"
          accent="#1d71e3"
          active={activeStatus === "All"}
          onClick={() => onSelect("All")}
        />
        <StatCard
          label="Present"
          value={present}
          icon="ti-user-check"
          iconClass="text-success"
          iconBg="#ecfdf5"
          accent="#16a34a"
          active={activeStatus === "Present"}
          onClick={() => onSelect("Present")}
        />
        <StatCard
          label="Late Login"
          value={late}
          icon="ti-alarm"
          iconClass="text-warning"
          iconBg="#fffbeb"
          accent="#f59e0b"
          active={activeStatus === "Late"}
          onClick={() => onSelect("Late")}
        />
        <StatCard
          label="Absent"
          value={absent}
          icon="ti-user-x"
          iconClass="text-danger"
          iconBg="#fef2f2"
          accent="#dc2626"
          active={activeStatus === "Absent"}
          onClick={() => onSelect("Absent")}
        />
      </div>
    </div>
  );
};

export default AttendanceAdminStats;
